import { AsyncPipe, DatePipe, TitleCasePipe, NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { terminologyCanonicalUrlBaseToken } from '../app.config';
import { buildPharmacyWalkInTagQuery } from '../medication-dispense/new-medication-dispense/new-medication-dispense.service';
import { AuthService } from '../shared/auth/auth.service';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { PatientAgePipe } from '../shared/pipes/patient-age.pipe';
import { PatientAddressPipe } from '../shared/pipes/patient-address.pipe';
import { PatientContactsPipe } from '../shared/pipes/patient-contacts.pipe';
import { PatientNamePipe } from '../shared/pipes/patient-name.pipe';
import { TableFilters } from '../shared/table-filtering.service';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { PatientRow, PatientSearchService } from './patient-search.service';
import { Patient } from 'fhir/r4';
import { UtilityService } from '../shared/utility.service';

@Component({
  selector: 'app-patients',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatDividerModule,
    TitleCasePipe,
    DatePipe,
    MatTableModule,
    AsyncPipe,
    PatientNamePipe,
    PatientAddressPipe,
    PatientContactsPipe,
    PatientAgePipe,
    EmptyStateComponent,
    MatIconModule,
    ReactiveFormsModule,
    MatPaginatorModule,
    TableHeaderComponent,
    NgClass
  ],
  templateUrl: './patients.component.html',
  styleUrl: './patients.component.scss'
})
export class PatientsComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly terminologyBase = inject(terminologyCanonicalUrlBaseToken);
  private readonly patientSearch = inject(PatientSearchService);

  readonly tableDataSource: MatTableDataSource<PatientRow> = this.patientSearch.dataSource;
  private tableDataSubscription?: Subscription;

  readonly loading$ = this.patientSearch.loading$;
  readonly total$ = this.patientSearch.total$;
  readonly displayedTotal$ = this.patientSearch.displayedTotal$;
  readonly unknownTotal$ = this.patientSearch.unknownTotal$;
  readonly error$ = this.patientSearch.error$;

  readonly patientSearchFieldOptions = [
    { label: 'Identifiers', value: 'identifier' },
    { label: 'Name', value: 'name' }
  ];
  selectedSearchField = this.patientSearchFieldOptions[0].value;

  patientsTableFilter = new Map<string, string[]>([
    ['gender', ['male', 'female', 'other', 'unknown']],
    ['encounter', ['Patients With Active Encounters']]
  ]);
  patientsFiltersFormControlObject: Record<string, FormGroup> = {};
  patientsTableDisplayedColumns: Array<keyof PatientRow | string> = [ 'hospitalId',  'name', 'phone', 'gender', 'age', 'address'];

  private activeFilters: Record<string, string[]> = {};
  private searchCriteria: { term: string; field: string } = { term: '', field: this.selectedSearchField };
  private queryParamSubscription?: Subscription;
  searchTerm = '';
  hasLoadedAnyRows = false;
  readonly pageSize = 25;
  // readonly maxCacheRows = 2000;
  paginatorPageIndex = 0;
  private readonly pharmacyWalkInTagQuery = buildPharmacyWalkInTagQuery(this.terminologyBase);
  private excludePharmacyWalkIns = true;

  currentEmptyState = {
    icon: 'people_outline',
    title: 'No active patients found',
    subtitle: 'There are currently no active patients in the system or all patients have been filtered out.'
  };

  canAdd$: Observable<boolean> = this.auth.user.pipe(map(() => this.auth.can('patient', 'add')));
  canExport$: Observable<boolean> = this.auth.user.pipe(map(() => this.auth.can('patient', 'viewAll')));

  ngOnInit(): void {
    const user = this.auth.user.getValue();
    const role = user?.role;
    const normalizedRole = (role || '').toString().toLowerCase();
    const shouldShowAllPatients = normalizedRole === 'pharmacy' || normalizedRole === 'cashier';
    this.excludePharmacyWalkIns = !shouldShowAllPatients;

    const patientResourceId = user?.['fhirResourceId'];
    if (role === 'patient' && patientResourceId) {
      this.router.navigate(['/app/patients', patientResourceId]);
      return;
    }

    this.initializeFilterFormControls();

    const practitionerId = ['doctor', 'nurse', 'midwife', 'healthcareassistant', 'remotedoctor'].includes(normalizedRole) 
      ? user?.['fhirResourceId'] 
      : undefined;

    this.patientSearch.init({
      excludePharmacyWalkIns: this.excludePharmacyWalkIns,
      pharmacyWalkInTagQuery: this.pharmacyWalkInTagQuery,
      pageSize: this.pageSize,
      // maxCacheRows: this.maxCacheRows
      practitionerId: practitionerId
    });

    this.tableDataSubscription = this.tableDataSource.connect().subscribe((rows) => {
      if (Array.isArray(rows) && rows.length > 0) {
        this.hasLoadedAnyRows = true;
      }
    });

    this.queryParamSubscription = this.route.queryParamMap.subscribe((params) => {
      this.applyQueryParamFilters(params);
    });

    this.emitFilters();
  }

  ngOnDestroy(): void {
    this.queryParamSubscription?.unsubscribe();
    this.tableDataSubscription?.unsubscribe();
    this.tableDataSource.disconnect();
    this.patientSearch.destroy();
  }

  onFilterChanged(event: { filterKey: string; values: string[] }): void {
    this.activeFilters[event.filterKey] = event.values ?? [];
    this.emitFilters();
  }

  onSearchCriteriaChanged(criteria: { term: string; field: string | null }): void {
    const normalizedField = criteria.field ?? this.patientSearchFieldOptions[0].value;
    const normalizedTerm = this.normalizeSearchTerm(criteria.term);
    this.selectedSearchField = normalizedField;
    this.searchCriteria = { term: normalizedTerm, field: normalizedField };
    this.searchTerm = normalizedTerm;
    this.emitFilters();
  }

  onPageChange(event: PageEvent): void {
    this.paginatorPageIndex = event.pageIndex;
    this.patientSearch.changePage(event.pageIndex);
  }
utility = inject(UtilityService);
  trackByPatientId(index: number, patient: PatientRow): string {
    return patient.id || index.toString();
  }
   getPatientHospitalId(p: Patient): string {
          return this.utility.getPatientHospitalId(p) || '';
      }

  showRow(row: PatientRow): void {
    const patientId = row.id || row.primaryIdentifier;

    if (!patientId) {
      console.error('No patient ID found in row:', row);
      return;
    }

    const role = (this.auth.user.getValue()?.role || '').toLowerCase();

    if (role === 'cashier') {
      this.router.navigate(['/app/invoice', patientId]);
      return;
    }

    const segments: string[] = [patientId];

    if (role === 'pharmacy' || role === 'pharmacist') {
      segments.push('medications');
    } else if (role === 'lab' || role === 'labtechnician' || role === 'alliedhealthprofessional') {
      segments.push('service-requests');
    }

    const extras: any = { relativeTo: this.route };
    if (row.appointmentId) {
      extras.queryParams = { appointmentId: row.appointmentId };
    }

    this.router.navigate(segments, extras);
  }

  private emitFilters(): void {
    const filters = this.buildFilterPayload();
    this.patientSearch.search(filters);
    this.paginatorPageIndex = 0;
  }

  private buildFilterPayload(): TableFilters {
    const filters: TableFilters = {};

    Object.entries(this.activeFilters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        if (key === 'encounter') {
          this.applyEncounterFilters(filters, values);
          return;
        }
        filters[key] = values.join(',');
      }
    });

    if (!this.searchCriteria.term) {
      return filters;
    }

    if (this.searchCriteria.field === 'identifier') {
      this.applyIdentifierFilters(filters, this.searchCriteria.term);
    } else {
      this.applyNameFilters(filters, this.searchCriteria.term);
    }

    return filters;
  }

  private applyIdentifierFilters(filters: TableFilters, rawTerm: string): void {
    const sanitized = this.sanitizeResourceId(rawTerm);
    if (!sanitized) {
      return;
    }

    filters['identifier'] = sanitized;
  }

  private applyNameFilters(filters: TableFilters, term: string): void {
    const tokens = term.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
      return;
    }

    if (tokens.length === 1) {
      filters['name:contains'] = tokens[0];
      return;
    }

    const family = tokens[tokens.length - 1];
    const given = tokens.slice(0, -1).join(' ');
    filters['name:contains'] = term;
    filters['family:contains'] = family;
    filters['given:contains'] = given;
  }

  private applyEncounterFilters(filters: TableFilters, values: string[]): void {
    const normalizedValues = values.map(value => value.toLowerCase());
    if (normalizedValues.includes('patients with active encounters')) {
      filters['_has:Encounter:subject:status'] = 'in-progress';
    }
  }

  private applyQueryParamFilters(params: ParamMap): void {
    const nextFilters: Record<string, string[]> = {};

    this.patientsTableFilter.forEach((options, filterKey) => {
      const selected = options.filter(option => this.queryParamMatchesFilterOption(params, filterKey, option));
      const formGroup = this.patientsFiltersFormControlObject[filterKey];

      if (formGroup) {
        options.forEach(option => {
          formGroup.get(option)?.setValue(selected.includes(option), { emitEvent: false });
        });
      }

      if (selected.length > 0) {
        nextFilters[filterKey] = selected;
      }
    });

    this.activeFilters = nextFilters;
    this.emitFilters();
  }

  private queryParamMatchesFilterOption(params: ParamMap, filterKey: string, option: string): boolean {
    const values = params.getAll(filterKey)
      .flatMap(value => value.split(','))
      .map(value => this.normalizeQueryFilterValue(filterKey, value))
      .filter(Boolean);

    return values.includes(this.normalizeQueryFilterValue(filterKey, option));
  }

  private normalizeQueryFilterValue(filterKey: string, value: string): string {
    const normalized = (value ?? '').toString().trim().toLowerCase();
    if (!normalized) return '';

    if (filterKey === 'encounter') {
      if (['active', 'active-encounter', 'active-encounters', 'in-progress', 'patients-with-active-encounters'].includes(normalized.replace(/\s+/g, '-'))) {
        return 'patients-with-active-encounters';
      }
    }

    return normalized.replace(/\s+/g, '-');
  }

  private initializeFilterFormControls(): void {
    this.patientsFiltersFormControlObject = {};

    this.patientsTableFilter.forEach((options, filterKey) => {
      const controls: Record<string, FormControl> = {};

      options.forEach((option) => {
        controls[option] = new FormControl(false);
      });

      this.patientsFiltersFormControlObject[filterKey] = new FormGroup(controls);
    });
  }

  private normalizeSearchTerm(term?: string | null): string {
    return term ? term.trim() : '';
  }

  private sanitizeResourceId(value: string): string {
    return value.replace(/^patient\//i, '').trim();
  }

  getTriageClass(level?: string): string {
    if (!level) return '';
    const normalized = level.toLowerCase();
    if (normalized.includes('1') || normalized.includes('red') || normalized.includes('resuscitation')) return 'triage-level-1';
    if (normalized.includes('2') || normalized.includes('yellow') || normalized.includes('emergent')) return 'triage-level-2';
    if (normalized.includes('3') || normalized.includes('urgent')) return 'triage-level-3';
    if (normalized.includes('4') || normalized.includes('less urgent')) return 'triage-level-4';
    if (normalized.includes('5') || normalized.includes('green') || normalized.includes('non-urgent')) return 'triage-level-5';
    if (normalized.includes('black') || normalized.includes('dead')) return 'triage-level-dead';
    return '';
  }
}
